import { useState, useEffect } from 'react'

import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TextField from '@mui/material/TextField'
import AutocompleteSelect from '../../../common/components/AutocompleteSelect'

import Alert from '@mui/material/Alert'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'

import { WALMART_SHOP_ID } from './constants'

interface Props {
    settings: Record<string, unknown>
    shops: Array<{ id: number; shop_name: string }>
    locations?: unknown[]
    setSettings: React.Dispatch<React.SetStateAction<Record<string, unknown>>>
}

export default function WalmartLocations({ settings, shops, locations = [], setSettings }: Props) {
    type Row = {
        shop_id: number | ''
        location: string
        netsuite_location: string
    }

        // Build options for dropdowns; prefer `locations` prop if provided
        const netsuiteLocationsSource = (Array.isArray(locations) && locations.length ? locations : (settings.locations as unknown[])) || []
    const netsuiteLocations: [string, string][] = (netsuiteLocationsSource as unknown[]).flatMap((item) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
            const obj = item as Record<string, unknown>
            const label = String(obj.name ?? obj.label ?? '')
            const value = String(obj.id ?? obj.value ?? '')
            return label ? [[label, value]] : []
        }
        return []
    })

    const existingLocations = (settings.locations_map as Record<string, unknown>) || {}
    const firstKey = Object.keys(existingLocations)[0]
    const initialFromSettings: Row[] = Array.isArray(existingLocations[firstKey])
        ? (existingLocations[firstKey] as unknown[]).map((triplet) => {
              if (Array.isArray(triplet) && triplet.length === 3)
                  return {
                      shop_id: typeof triplet[0] === 'number' ? triplet[0] : '',
                      location: String(triplet[1] ?? ''),
                      netsuite_location: String(triplet[2] ?? ''),
                  }
              return { shop_id: '', location: '', netsuite_location: '' }
          })
        : []

    const [rows, setRows] = useState<Row[]>(() => {
        if (initialFromSettings.length) return initialFromSettings
        return shops.length > 0
            ? shops.map((s) => ({ shop_id: s.id, location: '', netsuite_location: '' }))
            : [{ shop_id: '', location: '', netsuite_location: '' }]
    })

    // Sync when settings or props change
    useEffect(() => {
        const existingLocations = (settings.locations_map as Record<string, unknown>) || {}
        const firstKey = Object.keys(existingLocations)[0]
        const initial: Row[] = Array.isArray(existingLocations[firstKey])
            ? (existingLocations[firstKey] as unknown[]).map((triplet) => {
                  if (Array.isArray(triplet) && triplet.length === 3)
                      return {
                          shop_id: typeof triplet[0] === 'number' ? triplet[0] : '',
                          location: String(triplet[1] ?? ''),
                          netsuite_location: String(triplet[2] ?? ''),
                      }
                  return { shop_id: '', location: '', netsuite_location: '' }
              })
            : []
        const newRows: Row[] = initial.length
            ? initial
            : shops.length > 0
            ? shops.map((s) => ({ shop_id: s.id, location: '', netsuite_location: '' }))
            : [{ shop_id: '', location: '', netsuite_location: '' }]
        setRows(newRows)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.locations_map, shops, locations])

    // Build connection dropdown options from all provided shops
    const shopOptions: [string, number][] = shops.map((s) => [s.shop_name, s.id])

    const handleChange = <K extends keyof Row>(idx: number, field: K, value: Row[K]) => {
        setRows((prev) => {
        const updated = prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r))
        setSettings((s: Record<string, unknown>) => ({
            ...s,
            locations_map: { ...(s.locations_map as Record<string, unknown>) ?? {}, default: updated.map((r) => [r.shop_id, r.location, r.netsuite_location]) },
        }))
        return updated
    })
    }

    const handleAdd = () => {
        setRows((prev): Row[] => {
            const newRow: Row = { shop_id: '', location: '', netsuite_location: '' }
            const updated: Row[] = [...prev, newRow]
            setSettings((s: Record<string, unknown>) => ({
                ...s,
                locations_map: {
                    ...(s.locations_map as Record<string, unknown>) ?? {},
                    default: updated.map((r) => [r.shop_id, r.location, r.netsuite_location]),
                },
            }))
            return updated
        })
    }

    const handleDelete = (idx: number) => {
        setRows((prev) => {
            const updated = prev.filter((_, i) => i !== idx)
            setSettings((s: Record<string, unknown>) => ({
                ...s,
                locations_map: {
                    ...(s.locations_map as Record<string, unknown>) ?? {},
                    default: updated.map((r) => [r.shop_id, r.location, r.netsuite_location]),
                },
            }))
            return updated
        })
    }

    const renderSelect = (
        idx: number,
        field: keyof Omit<Row, 'location'>,
        options: [string, string | number][]
    ) => {
        const optionObjs = options.map(([label, value]) => ({ label, value }))
        return (
            <AutocompleteSelect
                options={optionObjs}
                value={rows[idx][field]}
                onChange={(val) => handleChange(idx, field, val as Row[typeof field])}
                fullWidth
            />
        )
    }

    return (
        <Paper elevation={0} sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="flex-end">
                <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                >
                    Add
                </Button>
            </Stack>
            {rows.length === 0 ? (
                <Alert severity="info" variant="outlined" sx={{ borderColor: 'grey.300', marginTop: '1rem' }}>
                    No locations defined yet
                </Alert>
            ) : (
                <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>
                                Connection
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>
                                Location
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>
                                Netsuite Location
                            </TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, idx) => (
                            <TableRow key={idx} sx={{ bgcolor: idx % 2 ? 'grey.50' : 'common.white' }}>
                                <TableCell sx={{ px: 1, py: 0.75 }}>
                                    {renderSelect(idx, 'shop_id', shopOptions)}
                                </TableCell>
                                <TableCell sx={{ px: 1, py: 0.75 }}>
                                    <TextField
                                        variant="outlined"
                                        color="secondary"
                                        size="small"
                                        margin="none"
                                        value={row.location}
                                        onChange={(e) => handleChange(idx, 'location', e.target.value)}
                                        fullWidth
                                        sx={{ '& .MuiOutlinedInput-root': { p: '1px 6px', height: 26 }, '& .MuiOutlinedInput-input': { p: 0, fontSize: '0.8125rem' } }}
                                    />
                                </TableCell>
                                <TableCell sx={{ px: 1, py: 0.75 }}>
                                    {renderSelect(idx, 'netsuite_location', netsuiteLocations)}
                                </TableCell>
                                <TableCell sx={{ width: 96, py: 0.75, px: 1 }}>
                                    <IconButton
                                        color="inherit"
                                        sx={{ color: 'grey.500' }}
                                        onClick={() => handleDelete(idx)}
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Paper>
    )
}
